import { LabelsSelector } from "../../store/selectors/LabelsSelector";
import {
  ImageData,
  LabelLine,
  LabelName,
  LabelPoint,
  LabelPolygon,
  LabelPolyline,
  LabelRect,
} from "../../store/labels/types";
import { filter } from "lodash";
import { store } from "../../index";
import {
  updateImageData,
  updateImageDataById,
  updateImageDataByIdwithoutundo,
  updateLabelNames,
  updateActiveLabelId,
  updateHighlightedLabelId,
  updateActiveLabelNameId,
  updateActiveLabelType,
} from "../../store/labels/actionCreators";
import { LabelType } from "../../data/enums/LabelType";
import { LabelStatus } from "../../data/enums/LabelStatus";
import { EditorActions } from "./EditorActions";
import { TrackActions } from "./TrackActions";

type SelectableDrawnLabel = {
  id: string;
  labelId: string | null;
  type: LabelType;
  createdAt?: number;
  timestamp?: number;
  orderNumber?: number;
  sourceIndex: number;
};

const HIDDEN_LABEL_STATUSES = new Set(["REJECTED", "DELETED"]);

export class LabelActions {
  public static deleteAllLabelsOnActiveImage(): void {
    const imageData = LabelsSelector.getActiveImageData();
    if (!imageData) {
      return;
    }

    const taskType = EditorActions.getTaskType();
    let newImageData: ImageData;

    if (taskType === "ALL") {
      newImageData = {
        ...imageData,
        labelRects: [],
        labelPolygons: [],
        labelPolylines: [],
        labelPoints: [],
        labelLines: [],
        labelTags: [],
      };
    } else {
      const activeType = LabelsSelector.getActiveLabelType();
      newImageData = { ...imageData };
      switch (activeType) {
        case LabelType.POINT:
          newImageData.labelPoints = [];
          break;
        case LabelType.LINE:
          newImageData.labelLines = [];
          break;
        case LabelType.POLYLINE:
          newImageData.labelPolylines = [];
          break;
        case LabelType.POLYGON:
          newImageData.labelPolygons = [];
          break;
        case LabelType.RECT:
        default:
          newImageData.labelRects = [];
          break;
      }
    }

    store.dispatch(updateImageDataById(imageData.image_id, newImageData));
    store.dispatch(updateActiveLabelId(null));
    EditorActions.fullRender();
  }

  public static deleteActiveLabel() {
    const activeImageData: ImageData | null = LabelsSelector.getActiveImageData();
    const activeLabelId: string | null = LabelsSelector.getActiveLabelId();
  
    if (!activeImageData || !activeLabelId) {
      console.warn("No active image or label selected.");
      return;
    }
  
    LabelActions.deleteImageLabelById(activeImageData.image_id, activeLabelId);
    store.dispatch(updateActiveLabelId(null));
    EditorActions.fullRender();
  }

  private static isHiddenLabel(label: {
    status?: LabelStatus | string | number;
  }): boolean {
    const status = label?.status;
    if (status == null || status === "") {
      return false;
    }

    const normalized = String(status).trim().toUpperCase();
    return HIDDEN_LABEL_STATUSES.has(normalized);
  }

  private static isVisibleForHotkey(label: {
    status?: LabelStatus | string | number;
    show?: boolean;
  }): boolean {
    if (label?.show === false) {
      return false;
    }

    return !LabelActions.isHiddenLabel(label);
  }

  private static compareSelectableLabels(
    a: SelectableDrawnLabel,
    b: SelectableDrawnLabel
  ): number {
    if (a.orderNumber != null && b.orderNumber != null && a.orderNumber !== b.orderNumber) {
      return a.orderNumber - b.orderNumber;
    }

    if (a.createdAt != null && b.createdAt != null && a.createdAt !== b.createdAt) {
      return a.createdAt - b.createdAt;
    }

    if (a.timestamp != null && b.timestamp != null && a.timestamp !== b.timestamp) {
      return a.timestamp - b.timestamp;
    }

    return a.sourceIndex - b.sourceIndex;
  }

  private static mapSelectableLabels<
    T extends {
      id: string | number;
      labelId?: string | null;
      createdAt?: number;
      timestamp?: number;
      orderNumber?: number;
      show?: boolean;
      status?: LabelStatus | string | number;
    }
  >(items: T[] | undefined, type: LabelType): SelectableDrawnLabel[] {
    return (items || [])
      .map((item, sourceIndex) => ({ item, sourceIndex }))
      .filter(({ item }) => LabelActions.isVisibleForHotkey(item))
      .map(({ item, sourceIndex }) => ({
        id: String(item.id),
        labelId: item.labelId ?? null,
        type,
        createdAt: item.createdAt,
        timestamp: item.timestamp,
        orderNumber: item.orderNumber,
        sourceIndex,
      }));
  }

  public static getSelectableDrawnLabels(
    imageData: ImageData,
    taskType: string = EditorActions.getTaskType()
  ): SelectableDrawnLabel[] {
    const activeType = LabelsSelector.getActiveLabelType();

    if (taskType !== "ALL") {
      switch (activeType) {
        case LabelType.POINT:
          return LabelActions.mapSelectableLabels(imageData.labelPoints, LabelType.POINT)
            .sort(LabelActions.compareSelectableLabels);
        case LabelType.LINE:
          return LabelActions.mapSelectableLabels(imageData.labelLines, LabelType.LINE)
            .sort(LabelActions.compareSelectableLabels);
        case LabelType.POLYLINE:
          return LabelActions.mapSelectableLabels(imageData.labelPolylines, LabelType.POLYLINE)
            .sort(LabelActions.compareSelectableLabels);
        case LabelType.POLYGON:
          return LabelActions.mapSelectableLabels(imageData.labelPolygons, LabelType.POLYGON)
            .sort(LabelActions.compareSelectableLabels);
        case LabelType.RECT:
        default:
          return LabelActions.mapSelectableLabels(imageData.labelRects, LabelType.RECT)
            .sort(LabelActions.compareSelectableLabels);
      }
    }

    const combined = [
      ...LabelActions.mapSelectableLabels(imageData.labelRects, LabelType.RECT),
      ...LabelActions.mapSelectableLabels(imageData.labelPolygons, LabelType.POLYGON),
      ...LabelActions.mapSelectableLabels(imageData.labelPolylines, LabelType.POLYLINE),
      ...LabelActions.mapSelectableLabels(imageData.labelPoints, LabelType.POINT),
      ...LabelActions.mapSelectableLabels(imageData.labelLines, LabelType.LINE),
    ];

    return combined.sort(LabelActions.compareSelectableLabels);
  }

  public static selectDrawnLabelByIndex(index: number): void {
    const imageData = LabelsSelector.getActiveImageData();
    if (!imageData) return;

    const labels = LabelActions.getSelectableDrawnLabels(imageData);
    if (index < 0 || index >= labels.length) return;

    const target = labels[index];

    if (target.type !== LabelsSelector.getActiveLabelType()) {
      store.dispatch(updateActiveLabelType(target.type));
      EditorActions.swapSupportRenderingEngine(target.type);
    }

    EditorActions.setViewPortActionsDisabledStatus(false);
    store.dispatch(updateActiveLabelId(String(target.id)));
    if (target.labelId) {
      store.dispatch(updateActiveLabelNameId(target.labelId));
    }
    EditorActions.fullRender();
  }

  private static persistVisibilityChange(
    imageId: string,
    newImageData: ImageData
  ): void {
    store.dispatch(updateImageDataByIdwithoutundo(imageId, newImageData));
    EditorActions.fullRender();
  }

  private static toggleLabelVisibility(
    imageId: string,
    collection: string,
    labelId: string
  ): void {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    if (!imageData) return;
    const labels = (imageData as any)[collection];
    if (!Array.isArray(labels)) return;
    const normalizedId = String(labelId);
    const newImageData = {
      ...imageData,
      [collection]: labels.map((item: any) =>
        String(item.id) === normalizedId
          ? { ...item, show: item.show === false }
          : item
      ),
    };
    LabelActions.persistVisibilityChange(imageData.image_id, newImageData);
  }
  //******************************************************************* */
  //color picker to change label names
  public static onChangeColor(id, name, color) {
    if (id) {
      const labels = LabelsSelector.getLabelNames().map((v) =>
        // eslint-disable-next-line eqeqeq
        id == v.id ? { id, name, color } : v
      );
      store.dispatch(updateLabelNames(labels));
    }

  }
  //********************************************************************** */
  public static deleteImageLabelById(imageId: string, labelId: string) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const normalizedId = String(labelId);

    if (imageData.labelRects?.some((label) => String(label.id) === normalizedId)) {
      LabelActions.deleteRectLabelById(imageId, labelId);
      return;
    }
    if (imageData.labelPolygons?.some((label) => String(label.id) === normalizedId)) {
      LabelActions.deletePolygonLabelById(imageId, labelId);
      return;
    }
    if (imageData.labelPolylines?.some((label) => String(label.id) === normalizedId)) {
      LabelActions.deletePolylineLabelById(imageId, labelId);
      return;
    }
    if (imageData.labelPoints?.some((label) => String(label.id) === normalizedId)) {
      LabelActions.deletePointLabelById(imageId, labelId);
      return;
    }
    if (imageData.labelLines?.some((label) => String(label.id) === normalizedId)) {
      LabelActions.deleteLineLabelById(imageId, labelId);
      return;
    }

    switch (LabelsSelector.getActiveLabelType()) {
      case LabelType.POINT:
        LabelActions.deletePointLabelById(imageId, labelId);
        break;
      case LabelType.LINE:
        LabelActions.deleteLineLabelById(imageId, labelId);
        break;
      case LabelType.RECT:
        LabelActions.deleteRectLabelById(imageId, labelId);
        break;
      case LabelType.POLYGON:
        LabelActions.deletePolygonLabelById(imageId, labelId);
        break;
      case LabelType.POLYLINE:
        LabelActions.deletePolylineLabelById(imageId, labelId);
        break;
    }
  }

  public static deleteRectLabelById(imageId: string, labelRectId: string) {
    if (LabelsSelector.isVideoTrackingEnabled() && !TrackActions.isSequenceReady()) {
      window.alert("Wait for every frame's annotations to load before editing tracks.");
      return;
    }

    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    if (!imageData) return;
    const normalizedId = String(labelRectId);
    if (!imageData.labelRects.some((rect) => String(rect.id) === normalizedId)) return;
    const newImageData = {
      ...imageData,
      labelRects: filter(imageData.labelRects, (currentLabel: LabelRect) => {
        return String(currentLabel.id) !== normalizedId;
      }),
      saved: false,
    };
    store.dispatch(updateImageDataById(imageData.image_id, newImageData));
    if (String(LabelsSelector.getActiveLabelId()) === normalizedId) {
      store.dispatch(updateActiveLabelId(null));
    }
    if (String(LabelsSelector.getHighlightedLabelId()) === normalizedId) {
      store.dispatch(updateHighlightedLabelId(null));
    }
    EditorActions.fullRender();
  }


  public static deleteSelectRectLabelById(imageId: string, list:any[]) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    if (!imageData) return;
    if (LabelsSelector.isVideoTrackingEnabled() && !TrackActions.isSequenceReady()) {
      window.alert("Wait for every frame's annotations to load before editing tracks.");
      return;
    }
    const ids = new Set((list || []).map(String));
    if (ids.size === 0) return;
    const labelRects = imageData.labelRects.filter((rect) => !ids.has(String(rect.id)));
    if (labelRects.length === imageData.labelRects.length) return;
    const newImageData = { ...imageData, labelRects, saved: false };
    store.dispatch(updateImageDataById(imageData.image_id, newImageData));
    if (ids.has(String(LabelsSelector.getActiveLabelId()))) {
      store.dispatch(updateActiveLabelId(null));
    }
    if (ids.has(String(LabelsSelector.getHighlightedLabelId()))) {
      store.dispatch(updateHighlightedLabelId(null));
    }
    EditorActions.fullRender();
  }

  public static deleteSelectPolygonsLabelById(imageId: string, list:any[]) {
    // window.alert(list)
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    let newImageData ={...imageData}
    list.map((i)=>{
      console.log(i)
      newImageData = {
        ...newImageData,
        labelPolygons: filter(newImageData.labelPolygons, (currentLabel: LabelPolygon) => {
          return currentLabel.id !== i;
        }),
      };
    })

  store.dispatch(updateImageDataById(imageData.image_id, newImageData));
}

  public static hideunhideRectLabelById(imageId: string, labelRectId: string) {
    LabelActions.toggleLabelVisibility(imageId, "labelRects", labelRectId);
  }
  public static hideselected(imageId: string, selected: any[]) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const activeType = LabelsSelector.getActiveLabelTypeName();
    const newImageData = {
      ...imageData,
      [activeType]: imageData[activeType].map((item) => {
        if (selected.some((el) => String(el) === String(item.id))) {
          return { ...item, show: item.show === false };
        }
        return item;
      })
    };
    LabelActions.persistVisibilityChange(imageData.image_id, newImageData);
  }
  
  
  public static unhideRectLabelById(imageId: string, labelRectId: string) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const newImageData = {
      ...imageData,
      labelRects: imageData.labelRects.map((item) => ({
        ...item,
        show: true,
      }))
    };
    LabelActions.persistVisibilityChange(imageData.image_id, newImageData);
  }
  public static hideunhidePolygonLabelById(imageId: string, labelPolygonId: string) {
    LabelActions.toggleLabelVisibility(imageId, "labelPolygons", labelPolygonId);
  }
  public static hideunhidePolylineLabelById(imageId: string, labelPolylineId: string) {
    LabelActions.toggleLabelVisibility(imageId, "labelPolylines", labelPolylineId);
  }
  public static hideUnHideActiveLabel() {
    const activeImageData: ImageData = LabelsSelector.getActiveImageData();
    const activeLabelId: string = LabelsSelector.getActiveLabelId();
    const activeLabelType: string = LabelsSelector.getActiveLabelTypeName();
    if (!activeImageData || !activeLabelId || !activeImageData[activeLabelType]) {
      return;
    }

    const newImageData = {
      ...activeImageData,
      [activeLabelType]: activeImageData[activeLabelType].map((item) => {
        if (String(item.id) === String(activeLabelId)) {
          return { ...item, show: item.show === false };
        }
        return item;
      })
    };
    LabelActions.persistVisibilityChange(activeImageData.image_id, newImageData);
  }
  public static hideRectLabelAll(hide) {
    const imageData: ImageData = LabelsSelector.getActiveImageData();
    const newImageData = {
      ...imageData,
      labelRects: imageData.labelRects.map((item) => ({
        ...item,
        show: hide,
      }))
    };
    LabelActions.persistVisibilityChange(imageData.image_id, newImageData);
  }
  public static hidePolyLabelAll(hide) {
    const imageData: ImageData = LabelsSelector.getActiveImageData();
    const newImageData = {
      ...imageData,
      labelPolygons: imageData.labelPolygons.map((item) => ({
        ...item,
        show: hide,
      }))
    };
    LabelActions.persistVisibilityChange(imageData.image_id, newImageData);
  }
  public static hideLabelAll(hide) {
    const label = LabelsSelector.getActiveLabelTypeName();
    const imageData: ImageData = LabelsSelector.getActiveImageData();
    const newImageData = {
      ...imageData,
      [label]: imageData[label].map((item) => ({
        ...item,
        show: hide,
      })),
    };
    LabelActions.persistVisibilityChange(imageData.image_id, newImageData);
  }
  public static hideLabelByClass(imageId: string, labellist) {
    const label = LabelsSelector.getActiveLabelTypeName();
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const newImageData = {
      ...imageData,
      [label]: imageData[label].map((item) => ({
        ...item,
        show: labellist.includes(item.labelId),
      }))
    };
    LabelActions.persistVisibilityChange(imageData.image_id, newImageData);
  }
  public static hideAll(imageId: string, labelRectId: string) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const newImageData = {
      ...imageData,
      labelRects: imageData.labelRects.map((item) => {
        if (item.id === labelRectId) {
          return { ...item, show: !(item.show != false) };
        }
        return item;
      })
    };
    LabelActions.persistVisibilityChange(imageData.image_id, newImageData);
  }
  public static deletePointLabelById(imageId: string, labelPointId: string) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const normalizedId = String(labelPointId);
    const newImageData = {
      ...imageData,
      labelPoints: filter(imageData.labelPoints, (currentLabel: LabelPoint) => {
        return String(currentLabel.id) !== normalizedId;
      }),
    };
    store.dispatch(updateImageDataById(imageData.image_id, newImageData));
  }
  public static hidePointLabelById(imageId: string, labelPointId: string) {
    LabelActions.toggleLabelVisibility(imageId, "labelPoints", labelPointId);
  }

  public static hideLineLabelById(imageId: string, labelLineId: string) {
    LabelActions.toggleLabelVisibility(imageId, "labelLines", labelLineId);
  }

  public static hideAudioLabelById(imageId: string, labelAudioId: string) {
    LabelActions.toggleLabelVisibility(imageId, "labelAudios", labelAudioId);
  }

  public static deleteLineLabelById(imageId: string, labelLineId: string) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const normalizedId = String(labelLineId);
    const newImageData = {
      ...imageData,
      labelLines: filter(imageData.labelLines, (currentLabel: LabelLine) => {
        return String(currentLabel.id) !== normalizedId;
      }),
    };
    store.dispatch(updateImageDataById(imageData.image_id, newImageData));
  }

  public static deletePolygonLabelById(
    imageId: string,
    labelPolygonId: string
  ) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const normalizedId = String(labelPolygonId);
    const newImageData = {
      ...imageData,
      labelPolygons: filter(
        imageData.labelPolygons,
        (currentLabel: LabelPolygon) => {
          return String(currentLabel.id) !== normalizedId;
        }
      ),
    };
    store.dispatch(updateImageDataById(imageData.image_id, newImageData));
  }
  public static deletePolylineLabelById(
    imageId: string,
    labelPolylineId: string
  ) {
    const imageData: ImageData = LabelsSelector.getImageDataById(imageId);
    const normalizedId = String(labelPolylineId);
    const newImageData = {
      ...imageData,
      labelPolylines: filter(
        imageData.labelPolylines,
        (currentLabel: LabelPolyline) => {
          return String(currentLabel.id) !== normalizedId;
        }
      ),
    };
    store.dispatch(updateImageDataById(imageData.image_id, newImageData));
  }

  public static removeLabelNames(labelNamesIds: string[]) {
    const imagesData: ImageData[] = LabelsSelector.getImagesData();
    const newImagesData: ImageData[] = imagesData.map(
      (imageData: ImageData) => {
        return LabelActions.removeLabelNamesFromImageData(
          imageData,
          labelNamesIds
        );
      }
    );
    store.dispatch(updateImageData(newImagesData));
  }

  private static removeLabelNamesFromImageData(
    imageData: ImageData,
    labelNamesIds: string[]
  ): ImageData {
    return {
      ...imageData,
      labelRects: imageData.labelRects.map((labelRect: LabelRect) => {
        if (labelNamesIds.includes(labelRect.id)) {
          return {
            ...labelRect,
            id: null,
          };
        } else {
          return labelRect;
        }
      }),
      labelPoints: imageData.labelPoints.map((labelPoint: LabelPoint) => {
        if (labelNamesIds.includes(labelPoint.id)) {
          return {
            ...labelPoint,
            id: null,
          };
        } else {
          return labelPoint;
        }
      }),
      labelPolygons: imageData.labelPolygons.map(
        (labelPolygon: LabelPolygon) => {
          if (labelNamesIds.includes(labelPolygon.id)) {
            return {
              ...labelPolygon,
              id: null,
            };
          } else {
            return labelPolygon;
          }
        }
      ),
      labelPolylines: imageData.labelPolylines.map(
        (labelPolyline: LabelPolyline) => {
          if (labelNamesIds.includes(labelPolyline.id)) {
            return {
              ...labelPolyline,
              id: null,
            };
          } else {
            return labelPolyline;
          }
        }
      ),
      labelTags: imageData.labelTags.filter((labelNameId: string) => {
        return !labelNamesIds.includes(labelNameId);
      }),
    };
  }

  public static labelExistsInLabelNames(label: string): boolean {
    const labelNames: LabelName[] = LabelsSelector.getLabelNames();
    return labelNames
      .map((labelName: LabelName) => labelName.name)
      .includes(label);
  }
}
